import { STATE_ALLOCATION_VALIDATION_LIMITS } from '../constants/stateAllocationConstants';

// Validate required field
export const validateRequired = (value, fieldName = 'Field') => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

// Validate amount
export const validateAmount = (amount, fieldName = 'Amount') => {
  if (amount === '' || amount === null || amount === undefined) {
    return null; // Allow empty amounts (will be treated as 0)
  }
  
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    return `${fieldName} must be a valid number`;
  }
  
  if (numAmount < STATE_ALLOCATION_VALIDATION_LIMITS.AMOUNT_MIN) {
    return `${fieldName} cannot be negative`;
  }
  
  if (numAmount > STATE_ALLOCATION_VALIDATION_LIMITS.AMOUNT_MAX) {
    return `${fieldName} cannot exceed ₹99,99,99 lakhs`;
  }
  
  return null;
};

// Validate project name
export const validateProjectName = (projectName) => {
  if (!projectName || !projectName.trim()) {
    return 'Project name is required';
  }
  
  const trimmedName = projectName.trim();
  
  if (trimmedName.length < STATE_ALLOCATION_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH) {
    return `Project name must be at least ${STATE_ALLOCATION_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH} characters`;
  }
  
  if (trimmedName.length > STATE_ALLOCATION_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH) {
    return `Project name cannot exceed ${STATE_ALLOCATION_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH} characters`;
  }
  
  return null;
};

// NEW: Validate funding pattern as string (e.g., "60:40", "10:20:70")
export const validateFundingPattern = (pattern) => {
  // Empty pattern is valid
  if (!pattern || pattern.trim() === '') {
    return null;
  }
  
  const trimmedPattern = pattern.trim();
  
  // Check length
  if (trimmedPattern.length > STATE_ALLOCATION_VALIDATION_LIMITS.FUNDING_PATTERN_MAX_LENGTH) {
    return `Funding pattern cannot exceed ${STATE_ALLOCATION_VALIDATION_LIMITS.FUNDING_PATTERN_MAX_LENGTH} characters`;
  }
  
  // Validate format: numbers separated by colons
  // const ratioPattern = /^(\d+)(:(\d+))*$/;
  // if (!ratioPattern.test(trimmedPattern)) {
  //   return 'Funding pattern';
  // }
  
  // Split and validate individual numbers
  const ratios = trimmedPattern.split(':');
  
  // Check if all parts are valid numbers
  // for (const ratio of ratios) {
  //   const num = parseInt(ratio);
  //   if (isNaN(num) || num < 0 || num > 100) {
  //     return 'Each ratio value must be a number between 0 and 100';
  //   }
  // }
  
  // Optional: Check if ratios sum to 100 (commented out as per your requirement)
  // const sum = ratios.reduce((total, ratio) => total + parseInt(ratio), 0);
  // if (sum !== 100) {
  //   return 'Ratio values should sum to 100';
  // }
  
  return null;
};

// Validate complete project allocation form (updated for string funding pattern)
export const validateProjectAllocation = (allocationData) => {
  const errors = {};
  
  // Department validation
  const departmentError = validateRequired(allocationData.department, 'Department');
  if (departmentError) errors.department = departmentError;
  
  // Project name validation
  const projectNameError = validateProjectName(allocationData.projectName);
  if (projectNameError) errors.projectName = projectNameError;
  
  // Amount validations
  const approvedError = validateAmount(allocationData.approvedAllocation, 'Approved allocation');
  if (approvedError) errors.approvedAllocation = approvedError;
  
  const clearedError = validateAmount(allocationData.clearedAgainstAllocation, 'Cleared against allocation');
  if (clearedError) errors.clearedAgainstAllocation = clearedError;
  
  const commonPoolError = validateAmount(allocationData.clearedFromCommonPool, 'Cleared from common pool');
  if (commonPoolError) errors.clearedFromCommonPool = commonPoolError;
  
  const additionalError = validateAmount(allocationData.additionalStateShare, 'Additional state share');
  if (additionalError) errors.additionalStateShare = additionalError;
  
  const totalError = validateAmount(allocationData.totalAmount, 'Total amount');
  if (totalError) errors.totalAmount = totalError;
  
  const centralShareError = validateAmount(allocationData.correspondingCentralShare, 'Corresponding central share');
  if (centralShareError) errors.correspondingCentralShare = centralShareError;
  
  const balanceError = validateAmount(allocationData.balanceStateShareAgainstAllocation, 'Balance state share');
  if (balanceError) errors.balanceStateShareAgainstAllocation = balanceError;
  
  // Funding pattern validation (now string-based)
  const fundingPatternError = validateFundingPattern(allocationData.fundingPattern);
  if (fundingPatternError) errors.fundingPattern = fundingPatternError;
  
  // Business logic validations
  const clearedAmount = parseFloat(allocationData.clearedAgainstAllocation) || 0;
  const approvedAmount = parseFloat(allocationData.approvedAllocation) || 0;
  
  if (clearedAmount > approvedAmount) {
    errors.clearedExceedsApproved = 'Cleared amount cannot exceed approved allocation';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Sanitize allocation data (updated for string funding pattern)
export const sanitizeAllocationData = (allocationData) => {
  const sanitized = { ...allocationData };
  
  // Trim string fields
  if (sanitized.projectName) sanitized.projectName = sanitized.projectName.trim();
  if (sanitized.department) sanitized.department = sanitized.department.trim();
  
  // Keep installment for backend compatibility but set default value
  if (!sanitized.installment) {
    sanitized.installment = 1; // Default installment value
  } else {
    sanitized.installment = parseInt(sanitized.installment);
  }
  
  // Convert amounts to numbers
  const amountFields = [
    'approvedAllocation', 'clearedAgainstAllocation', 'clearedFromCommonPool',
    'additionalStateShare', 'totalAmount', 'correspondingCentralShare',
    'balanceStateShareAgainstAllocation'
  ];
  
  amountFields.forEach(field => {
    if (sanitized[field] === '' || sanitized[field] === null || sanitized[field] === undefined) {
      sanitized[field] = 0;
    } else {
      sanitized[field] = parseFloat(sanitized[field]) || 0;
    }
  });
  
  // Sanitize funding pattern (now as string)
  if (sanitized.fundingPattern) {
    sanitized.fundingPattern = sanitized.fundingPattern.toString().trim();
  } else {
    sanitized.fundingPattern = '';
  }
  
  return sanitized;
};

export default {
  validateRequired,
  validateAmount,
  validateProjectName,
  validateFundingPattern,
  validateProjectAllocation,
  sanitizeAllocationData
};